/**
 * Pure helpers for the web and mobile playtest UIs. They sit beside the engine,
 * not inside it: the engine never sees card names, types or images (design D1).
 */
import type {
  HistoryEntry,
  HistorySource,
  InstanceId,
  MoveTarget,
  ReshuffleZone,
  SessionAction,
  SessionEntry,
  SessionResult,
  SessionState,
  Zone,
} from './session';
import { applyAction, MOVE_TARGETS as ENGINE_MOVE_TARGETS, ZONES } from './session';

/** One main-zone row from `GET /collections/:id?zone=main&include=card`. Structural only. */
export interface CollectionRow {
  readonly scryfall_id: string;
  readonly quantity: number;
  readonly is_commander: boolean;
  readonly is_signature_spell: boolean;
  readonly card: CollectionCard | null;
}

export interface CollectionCard {
  readonly name: string;
  readonly type_line: string;
  readonly primary_type?: string | null;
  readonly image_small: string | null;
}

/** Display details for one card, keyed by scryfall id beside the session. */
export interface CardView {
  readonly name: string;
  readonly typeLine: string;
  readonly primaryType: string | null;
  readonly imageSmall: string | null;
}

export type PeekCountError = 'not-a-whole-number' | 'below-one' | 'above-library';

export interface HistoryGame {
  readonly game: number;
  /** In `seq` order. */
  readonly entries: readonly HistoryEntry[];
}

/** Panel labels. Both UIs import these so the text is identical. */
export const HISTORY_LABELS: Readonly<Record<HistorySource, string>> = {
  opening: 'Opening hand',
  draw: 'Draw',
  mulligan: 'Mulligan redraw',
};

/** Targets that prompt when a commander outside the command zone is moved to one of them by a single move. */
const REPLACEMENT_PROMPT_TARGETS: readonly MoveTarget[] = [
  'graveyard',
  'exile',
  'hand',
  'library-top',
  'library-bottom',
];

const WHOLE_NUMBER = /^\d+$/;
const MIN_PEEK_COUNT = 1;

/** Maps collection rows to session entries, in order. Quantities are kept, not expanded. */
export function toSessionEntries(rows: readonly CollectionRow[]): SessionEntry[] {
  return rows.map((row) => ({
    scryfallId: row.scryfall_id,
    quantity: row.quantity,
    is_commander: row.is_commander,
    is_signature_spell: row.is_signature_spell,
  }));
}

/** Display details keyed by scryfall id. Rows without a card are omitted. */
export function cardViewsOf(rows: readonly CollectionRow[]): ReadonlyMap<string, CardView> {
  const views = new Map<string, CardView>();
  for (const row of rows) {
    if (row.card) {
      views.set(row.scryfall_id, {
        name: row.card.name,
        typeLine: row.card.type_line,
        imageSmall: row.card.image_small,
        primaryType: row.card.primary_type ?? null,
      });
    }
  }
  return views;
}

/**
 * Validates the View top N input text. Returns null when valid. Surrounding
 * whitespace is ignored. Rejects non-whole numbers, 0, and counts above the library.
 */
export function peekCountError(input: string, librarySize: number): PeekCountError | null {
  const text = input.trim();
  if (!WHOLE_NUMBER.test(text)) return 'not-a-whole-number';
  const count = Number(text);
  if (count < MIN_PEEK_COUNT) return 'below-one';
  if (count > librarySize) return 'above-library';
  return null;
}

/** History grouped by game, games ascending. Entries keep their stored (`seq`) order. */
export function historyByGame(state: SessionState): HistoryGame[] {
  const games = new Map<number, HistoryEntry[]>();
  for (const entry of state.history) {
    const list = games.get(entry.game);
    if (list) {
      list.push(entry);
    } else {
      games.set(entry.game, [entry]);
    }
  }
  return [...games].map(([game, entries]) => ({ game, entries }));
}

/**
 * True when a single move of instance `id` to `to` should show the replacement
 * prompt: the instance is a command-zone card outside command, and the target
 * is graveyard, exile, hand, library-top or library-bottom.
 */
export function movePromptsForCommandCard(
  state: SessionState,
  id: number,
  to: MoveTarget,
): boolean {
  const instance = state.instances[id];
  if (!instance || !instance.commandZoneCard) return false;
  if (state.zones.command.includes(id)) return false;
  return REPLACEMENT_PROMPT_TARGETS.includes(to);
}

/**
 * True when the reshuffle sheet should show the O3 toggle: at least one checked
 * zone holds a command-zone card.
 */
export function reshuffleToggleVisible(
  state: SessionState,
  checked: readonly ReshuffleZone[],
): boolean {
  return checked.some((zone) =>
    state.zones[zone].some((id) => state.instances[id]?.commandZoneCard === true),
  );
}

/** Refusal text for a rejected action or drop. Both UIs show these, so the wording matches. */
export const RESULT_MESSAGES: Readonly<Record<Exclude<SessionResult, 'ok'>, string>> = {
  'not-kept': 'Keep your opening hand first.',
  'nothing-to-draw': 'The library is empty.',
  'not-deciding': 'The opening hand is already decided.',
  'empty-hand': 'There is no hand to mulligan.',
  'wrong-bottom-count': 'Choose exactly the cards you owe to the bottom.',
  'not-in-hand': 'That card is not in your hand.',
  'unknown-instance': 'That card is not in this game.',
  'invalid-zone': 'That zone is not allowed for this card.',
  'not-command-card': 'Only commander cards can go to the command zone.',
  'same-zone': 'That card is already there.',
  'not-in-command': 'That card is not in the command zone.',
  'invalid-order': 'The top cards changed. Try again.',
  'invalid-destination': 'That card cannot go there.',
  'not-on-battlefield': 'That card is not on the battlefield.',
  'no-change': 'Nothing changed.',
  'invalid-host': 'A card cannot attach to itself or to a card that is attached to another card.',
  'has-attachments': 'Detach the cards on this card first.',
  'not-attached': 'That card is not attached.',
};

/** Zone names for display. Both UIs import these so the text is identical. */
export const ZONE_LABELS: Readonly<Record<Zone, string>> = {
  library: 'Library',
  hand: 'Hand',
  battlefield: 'Battlefield',
  graveyard: 'Graveyard',
  exile: 'Exile',
  command: 'Command zone',
};

/** Display text for every destination a move can name. Typed, so a missing key is a compile error. */
export const MOVE_TARGET_LABELS: Readonly<Record<MoveTarget, string>> = {
  hand: ZONE_LABELS.hand,
  battlefield: ZONE_LABELS.battlefield,
  graveyard: ZONE_LABELS.graveyard,
  exile: ZONE_LABELS.exile,
  command: ZONE_LABELS.command,
  'library-top': 'Top of library',
  'library-bottom': 'Bottom of library',
};

/** One destination a single move can name, with its label. */
export interface MoveTargetOption {
  readonly to: MoveTarget;
  readonly label: string;
}

/**
 * Every destination a single move can name, in display order. The engine's order is kept,
 * except the command zone, which is listed last.
 */
const COMMAND_LAST: MoveTarget = 'command';
export const MOVE_TARGETS: readonly MoveTargetOption[] = [
  ...ENGINE_MOVE_TARGETS.filter((to) => to !== COMMAND_LAST),
  COMMAND_LAST,
].map((to) => ({ to, label: MOVE_TARGET_LABELS[to] }));

/** The reshuffle checklist order. The command zone is never offered. */
export const RESHUFFLE_SOURCE_ORDER: readonly ReshuffleZone[] = ['hand', 'battlefield', 'graveyard', 'exile'];

/** The zone holding an instance, or undefined when no zone holds it. */
export function zoneOf(state: SessionState, id: InstanceId): Zone | undefined {
  return ZONES.find((zone) => state.zones[zone].includes(id));
}

/**
 * Destinations offered for a card. The current zone is never offered; the command
 * zone only for a command-zone card outside command; a library end only where the
 * engine accepts the move (a card already at that end gets `no-change`).
 */
export function moveTargetsFor(
  state: SessionState,
  instanceId: InstanceId,
): readonly MoveTargetOption[] {
  const zone = zoneOf(state, instanceId);
  if (zone === undefined) return [];
  const commandZoneCard = state.instances[instanceId]?.commandZoneCard === true;
  return MOVE_TARGETS.filter(({ to }) => {
    if (to === 'library-top' || to === 'library-bottom') {
      return applyAction(state, { type: 'move', instanceId, to }).result === 'ok';
    }
    if (to === 'command') return commandZoneCard && zone !== 'command';
    return to !== zone;
  });
}

/**
 * Whether the engine accepts a draw from this state. A dry run, so the reshuffle
 * and empty-library rules live only in the engine.
 */
export function canDraw(state: SessionState): boolean {
  return applyAction(state, { type: 'draw' }).result === 'ok';
}

/** The fields of a card detail payload that `cardFacesOf` reads. */
export interface FacedCard {
  readonly oracle_text?: string | null;
  readonly card_faces?: readonly unknown[] | null;
}

type FaceOf<Card> = Card extends { readonly card_faces?: readonly (infer Face)[] | null } ? Face : never;

/**
 * Faces to show for a card detail payload: each face of a multi-face card that
 * has no oracle text of its own, else the card itself.
 */
export function cardFacesOf<Card extends FacedCard>(card: Card): readonly (FaceOf<Card> | Card)[] {
  if (Array.isArray(card.card_faces) && card.card_faces.length > 0 && !card.oracle_text) {
    return card.card_faces as readonly FaceOf<Card>[];
  }
  return [card];
}

/** Swaps the entry at `index` with its neighbour at `index + offset`. An edge returns the same array. */
export function swapWithNeighbour<T>(ids: readonly T[], index: number, offset: number): readonly T[] {
  const target = index + offset;
  if (target < 0 || target >= ids.length) return ids;
  const next = [...ids];
  [next[index], next[target]] = [next[target] as T, next[index] as T];
  return next;
}

/** The top `count` library cards in library order, clamped to the library size. */
export function viewedIds(state: SessionState, count: number): readonly InstanceId[] {
  return state.zones.library.slice(0, count);
}

/** Toggles one reshuffle source and keeps the checklist order. */
export function toggleReshuffleZone(
  checked: readonly ReshuffleZone[],
  zone: ReshuffleZone,
): readonly ReshuffleZone[] {
  const next = checked.includes(zone) ? checked.filter((z) => z !== zone) : [...checked, zone];
  return RESHUFFLE_SOURCE_ORDER.filter((z) => next.includes(z));
}

/** Battlefield section keys, in display order (design D8). */
export type BattlefieldSectionKey =
  | 'creatures'
  | 'planeswalkers'
  | 'artifacts'
  | 'enchantments'
  | 'other'
  | 'lands';

/** A non-empty battlefield section. `ids` keep battlefield order. */
export interface BattlefieldSection {
  readonly key: BattlefieldSectionKey;
  readonly label: string;
  readonly ids: readonly InstanceId[];
}

/** The lands section key. Lands sort last and are set off by a divider on both UIs. */
export const LANDS_SECTION_KEY = 'lands' as const;

/** Section order and labels (D8). Lands sort last; a null `primaryType` is the catch-all. */
const BATTLEFIELD_SECTIONS: readonly {
  readonly key: BattlefieldSectionKey;
  readonly label: string;
  readonly primaryType: string | null;
}[] = [
  { key: 'creatures', label: 'Creatures', primaryType: 'Creature' },
  { key: 'planeswalkers', label: 'Planeswalkers', primaryType: 'Planeswalker' },
  { key: 'artifacts', label: 'Artifacts', primaryType: 'Artifact' },
  { key: 'enchantments', label: 'Enchantments', primaryType: 'Enchantment' },
  { key: 'other', label: 'Other', primaryType: null },
  { key: LANDS_SECTION_KEY, label: 'Lands', primaryType: 'Land' },
];

/**
 * Groups battlefield instances into display sections (design D8). Pass only
 * unattached ids: attached cards are drawn under their host. `primaryTypeOf`
 * gives the card's primary type (e.g. "Creature"); a missing or unlisted type
 * falls into "Other". Empty sections are omitted.
 */
export function groupBattlefield(
  battlefieldIds: readonly InstanceId[],
  primaryTypeOf: (id: InstanceId) => string | undefined,
): BattlefieldSection[] {
  const keyed = battlefieldIds.map((id) => ({
    id,
    key: BATTLEFIELD_SECTIONS.find((s) => s.primaryType === primaryTypeOf(id))?.key ?? 'other',
  }));
  return BATTLEFIELD_SECTIONS.map(({ key, label }) => ({
    key,
    label,
    ids: keyed.filter((entry) => entry.key === key).map((entry) => entry.id),
  })).filter((section) => section.ids.length > 0);
}

/** Card ids attached to `hostId`, in stacking order (attachment list order). */
export function attachmentsOf(state: SessionState, hostId: InstanceId): InstanceId[] {
  return state.attachments.filter((entry) => entry.to === hostId).map((entry) => entry.instanceId);
}

/** Battlefield ids not attached to a host, in battlefield order. The input for groupBattlefield. */
export function unattachedBattlefieldIds(state: SessionState): InstanceId[] {
  const attached = new Set(state.attachments.map((entry) => entry.instanceId));
  return state.zones.battlefield.filter((id) => !attached.has(id));
}

/** True when instance `id` is tapped. */
export function isTapped(state: SessionState, id: InstanceId): boolean {
  return state.tapped.has(id);
}

/**
 * One tile on the land row (design D18). `key` is stable across renders. `instanceIds`
 * keep battlefield order, and `count` is `instanceIds.length`.
 */
export interface LandStack {
  readonly key: string;
  readonly cardId: string;
  readonly tapped: boolean;
  readonly instanceIds: readonly InstanceId[];
  readonly count: number;
}

/** Key prefix for a land that is shown alone. Stack keys end in `:true` or `:false`, so the two never collide. */
const SINGLE_LAND_KEY_PREFIX = 'instance:';

/**
 * Stacks battlefield lands for display (design D18). Every battlefield id in `landIds`
 * appears in exactly one returned stack, and ids not on the battlefield are ignored.
 *
 * - A land that is unattached and hosts nothing joins the stack for its (cardId, tapped)
 *   pair. Lands of one printing stack together; a different printing does not.
 * - An attached land, or a land that hosts attachments, is returned alone as a
 *   one-instance stack. The caller draws it as an individual tile, as before.
 *
 * Stacks follow first appearance in battlefield order. Pure: no engine state changes.
 */
export function stackLands(state: SessionState, landIds: readonly InstanceId[]): LandStack[] {
  const wanted = new Set(landIds);
  const attached = new Set(state.attachments.map((entry) => entry.instanceId));
  const hosts = new Set(state.attachments.map((entry) => entry.to));
  const stacks = new Map<string, { cardId: string; tapped: boolean; instanceIds: InstanceId[] }>();
  for (const id of state.zones.battlefield) {
    const instance = state.instances[id];
    if (!wanted.has(id) || instance === undefined) continue;
    const tapped = isTapped(state, id);
    const solo = attached.has(id) || hosts.has(id);
    const key = solo ? `${SINGLE_LAND_KEY_PREFIX}${id}` : `${instance.cardId}:${tapped}`;
    const stack = stacks.get(key);
    if (stack) {
      stack.instanceIds.push(id);
    } else {
      stacks.set(key, { cardId: instance.cardId, tapped, instanceIds: [id] });
    }
  }
  return Array.from(stacks, ([key, stack]) => ({
    key,
    cardId: stack.cardId,
    tapped: stack.tapped,
    instanceIds: stack.instanceIds,
    count: stack.instanceIds.length,
  }));
}

/**
 * Builds the `primaryTypeOf` callback for groupBattlefield from card views. An
 * instance with no view, or a view whose primaryType is null, gives undefined
 * and falls into Other.
 */
export function primaryTypeOfInstance(
  state: SessionState,
  cardViews: ReadonlyMap<string, CardView>,
): (id: InstanceId) => string | undefined {
  return (id) => {
    const cardId = state.instances[id]?.cardId;
    if (cardId === undefined) return undefined;
    return cardViews.get(cardId)?.primaryType ?? undefined;
  };
}

/** A drop target: a zone, or another card (which attaches the dragged card). */
export type DropTarget =
  | { readonly kind: 'zone'; readonly zone: MoveTarget }
  | { readonly kind: 'card'; readonly instanceId: InstanceId };

/** Why a drop is refused: the engine's refusal, or a pre-check that names the drop. */
export type DropRejection = Exclude<SessionResult, 'ok'>;

/**
 * What a drop means. `action`: send it. `prompt`: ask where the command-zone
 * card goes (the chosen move is the only action sent). `reject`: send nothing.
 */
export type DropResult =
  | { readonly kind: 'action'; readonly action: SessionAction }
  | { readonly kind: 'prompt'; readonly instanceId: InstanceId; readonly to: MoveTarget }
  | { readonly kind: 'reject'; readonly reason: DropRejection };

/** The action when the engine accepts it on a dry run, else its refusal. */
function actionResult(state: SessionState, action: SessionAction): DropResult {
  const { result } = applyAction(state, action);
  return result === 'ok' ? { kind: 'action', action } : rejectResult(result);
}

function rejectResult(reason: DropRejection): DropResult {
  return { kind: 'reject', reason };
}

/**
 * Interprets dropping card `source` on `target` (design D12). Rules apply in
 * order and the first match wins. Each action is dry-run through the engine, so
 * an accepted drop is one the engine applies, and a rejected drop changes nothing.
 */
export function resolveDrop(
  state: SessionState,
  source: InstanceId,
  target: DropTarget,
): DropResult {
  const from = zoneOf(state, source);
  if (from === undefined) return rejectResult('unknown-instance');
  if (target.kind === 'card') {
    if (zoneOf(state, target.instanceId) !== 'battlefield') {
      return rejectResult('not-on-battlefield');
    }
    if (from === 'battlefield') {
      return actionResult(state, { type: 'attach', instanceId: source, to: target.instanceId });
    }
    return actionResult(state, { type: 'move', instanceId: source, to: 'battlefield' });
  }
  const to = target.zone;
  if (to === 'battlefield') {
    return from === 'battlefield'
      ? rejectResult('same-zone')
      : actionResult(state, { type: 'move', instanceId: source, to });
  }
  if (to === 'command') {
    if (state.instances[source]?.commandZoneCard !== true) return rejectResult('not-command-card');
    return from === 'command'
      ? rejectResult('same-zone')
      : actionResult(state, { type: 'move', instanceId: source, to });
  }
  if (to === from) return rejectResult('same-zone');
  // The dry run comes first: an engine refusal (such as 'no-change' at a library end) never prompts.
  const accepted = actionResult(state, { type: 'move', instanceId: source, to });
  if (accepted.kind === 'reject') return accepted;
  if (movePromptsForCommandCard(state, source, to)) {
    return { kind: 'prompt', instanceId: source, to };
  }
  return accepted;
}

/**
 * Whether the engine accepts attaching `instanceId` to `hostId`. A dry run, so
 * the attach rules live only in the engine.
 */
export function canAttach(
  state: SessionState,
  instanceId: InstanceId,
  hostId: InstanceId,
): boolean {
  return applyAction(state, { type: 'attach', instanceId, to: hostId }).result === 'ok';
}

/**
 * Battlefield cards the engine accepts as a host for `instanceId`, in battlefield
 * order. Empty means the Attach control should be hidden.
 */
export function attachCandidates(state: SessionState, instanceId: InstanceId): InstanceId[] {
  return state.zones.battlefield.filter(hostId => canAttach(state, instanceId, hostId));
}
